import { useEffect, useMemo, useState } from "react";
import { Lightbulb, TrendingUp } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { matchesQuery } from "@/lib/categories";

const IDEAS = [
  ["Activities", ["Zip lining", "Parasailing", "Hot air balloon", "The fair", "Go-karts", "Mini golf", "Horseback riding", "ATV riding", "Paintball", "Topgolf", "Virtual gaming"]],
  ["Shows", ["Magic shows", "Movie theaters", "Comedy", "Circus"]],
  ["Outdoor", ["Dinner in the Sky", "Hiking trails", "Waterfalls", "Beaches", "Gardens", "Lagoons", "Freshwater springs"]],
  ["On the water", ["Whitewater rafting", "Jet ski", "Fishing charter", "Kayak"]],
  ["Food & drink", ["Cafés", "Breakfast & brunch", "Wine tasting"]],
  ["Family fun", ["Arcades", "Bowling", "Escape rooms", "Skating", "Festivals"]],
];

const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function IdeaChip({ label, count, onPick, prefix = "search-idea" }) {
  const none = !count;
  return (
    <button type="button" data-testid={`${prefix}-${slug(label)}`} onClick={() => onPick(label)}
      className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors ${none
        ? "bg-transparent border-dashed border-[#26334D] text-slate-500 hover:text-slate-300"
        : "bg-[#151C2C] border-[#26334D] text-slate-200 hover:border-[#00E5A3] hover:text-[#00E5A3]"}`}>
      {label}
      {!none && <span data-testid={`${prefix}-count-${slug(label)}`} className="font-mono-vv text-[11px] text-[#00E5A3]">{count}</span>}
    </button>
  );
}

function Group({ title, items, onPick, icon: Icon, prefix, testid }) {
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5 text-[#FF3B6B]" />}{title}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(([label, count]) => <IdeaChip key={label} label={label} count={count} onPick={onPick} prefix={prefix} />)}
      </div>
    </div>
  );
}

export function SearchIdeas({ onPick, listings = [], city }) {
  const [open, setOpen] = useState(false);
  const [trend, setTrend] = useState({ scope: "city", items: [] });
  useEffect(() => {
    if (open && city) api.get("/search/trending", { params: { city } }).then((r) => setTrend(r.data)).catch(() => {});
  }, [open, city]);

  const count = useMemo(() => (q) => listings.filter((l) => matchesQuery(l, q)).length, [listings]);
  const groups = useMemo(() => IDEAS.map(([g, items]) => [g, items.map((i) => [i, count(i)])]), [count]);
  const missing = groups.flatMap(([, items]) => items.filter(([, n]) => !n));
  const trending = trend.items.map((t) => [t.q, count(t.q)]);
  const pick = (q) => { onPick(q); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="search-ideas-button"
          className="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-xl text-sm text-[#FFB800] border border-[#FFB800]/30 hover:bg-[#FFB800]/10 transition-colors">
          <Lightbulb className="h-4 w-4" /> Ideas
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" data-testid="search-ideas-panel"
        className="w-[min(92vw,26rem)] max-h-[70vh] overflow-y-auto rounded-2xl border-[#26334D] bg-[#111827] text-white p-5 shadow-2xl">
        <p className="text-sm font-semibold">Not sure what to look for?</p>
        <p className="text-xs text-slate-400 mt-0.5">Tap an idea to search it. Numbers show how many spots we found here.</p>
        <div className="mt-4 space-y-4">
          <Group title={trend.scope === "city" ? "Popular this week here" : "Popular this week"} icon={TrendingUp}
            items={trending} onPick={pick} prefix="trending-idea" testid="ideas-group-trending" />
          {groups.map(([g, items]) => (
            <Group key={g} title={g} items={items.filter(([, n]) => n)} onPick={pick} testid={`ideas-group-${slug(g)}`} />
          ))}
          <Group title="Not found here yet · tap to search the whole city" items={missing} onPick={pick} testid="ideas-group-missing" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
